import { useRef } from 'react'
import { useQuery } from '@apollo/client'
import { LessonAvailabilityQuery } from 'pages/schedule/queries'
const { DateTime } = require('luxon')

export const aggregateByDate = (timeSlots, tz) => {
  return timeSlots.reduce((acc, ts) => {
    const date = DateTime.fromISO(ts, { zone: tz }).toISODate()
    if (!(date in acc)) {
      acc[date] = []
    }
    acc[date].push(ts)
    return acc
  }, {})
}

export const aggregateByPeriod = (timeSlots, tz) => {
  return timeSlots.reduce(
    (acc, ts) => {
      const period = DateTime.fromISO(ts, { zone: tz }).toFormat('a')
      acc[period].push(ts)
      return acc
    },
    { AM: [], PM: [] },
  )
}

/**
 * Splits time slots into morning (before 12:00), afternoon (12:00-16:59) and
 * evening (17:00 onward), judged on the wall clock in `tz`.
 */
export const groupSlotsByDayPart = (timeSlots, tz) => {
  return timeSlots.reduce(
    (acc, ts) => {
      const { hour } = DateTime.fromISO(ts, { zone: tz })
      if (hour < 12) acc.morning.push(ts)
      else if (hour < 17) acc.afternoon.push(ts)
      else acc.evening.push(ts)
      return acc
    },
    { morning: [], afternoon: [], evening: [] },
  )
}

/** Maps each ISO date from `aggregateByDate` output to its slot count. */
export const countByDate = timeSlotsByDate =>
  Object.keys(timeSlotsByDate).reduce((acc, date) => {
    acc[date] = timeSlotsByDate[date].length
    return acc
  }, {})

/** Slot label such as `7:00 AM` (not zero padded) in the facility timezone. */
export const formatSlotLabel = (ts, tz) =>
  DateTime.fromISO(ts, { zone: tz })
    .setLocale('en-US')
    .toFormat('h:mm a')

/**
 * One entry per calendar day from `start` through `end` (inclusive, in `tz`)
 * for the date strip. `openCount` comes from `countsByDate` keyed by ISO date.
 */
export const buildDateStripDays = ({
  start,
  end,
  timezone,
  countsByDate = {},
  now = DateTime.local(),
}) => {
  const first = start.setZone(timezone).startOf('day')
  const last = end.setZone(timezone).startOf('day')
  const todayIso = now.setZone(timezone).toISODate()
  const days = []
  for (let day = first; day <= last; day = day.plus({ days: 1 })) {
    const iso = day.toISODate()
    const openCount = countsByDate[iso] || 0
    const localized = day.setLocale('en-US')
    days.push({
      iso,
      dateTime: day,
      isToday: iso === todayIso,
      weekdayLabel: localized.toFormat('ccc').toUpperCase(),
      dayNumber: localized.toFormat('d'),
      fullLabel: localized.toFormat('cccc, LLLL d'),
      openCount,
      isClosed: openCount === 0,
    })
  }
  return days
}

export const AVAILABILITY_LOOKAHEAD_MONTHS = 1

export const availabilityMaxEndDateTime = (currentTime = DateTime.local()) =>
  currentTime.plus({ months: AVAILABILITY_LOOKAHEAD_MONTHS }).endOf('month')

export const parseSelectedDayUtc = selectedDay => {
  if (!selectedDay) return null
  const parsed = DateTime.fromISO(selectedDay, { zone: 'utc' })
  if (!parsed.isValid) return null
  return parsed.startOf('day')
}

export const selectedDayQueryEndBound = selectedDay => {
  const parsedUtc = parseSelectedDayUtc(selectedDay)
  if (!parsedUtc) return null
  return parsedUtc
    .endOf('month')
    .plus({ days: 1 })
    .startOf('day')
}

export const isSelectedDayWithinAvailabilityCap = (
  selectedDay,
  currentTime = DateTime.local(),
) => {
  const parsedUtc = parseSelectedDayUtc(selectedDay)
  if (!parsedUtc) return false
  const maxEndDate = availabilityMaxEndDateTime(currentTime).toISODate()
  return parsedUtc.toISODate() <= maxEndDate
}

export const dateRangeForMonth = (month, currentTime) => {
  const startDateTime = DateTime.max(currentTime, month)
  const endDateTime = startDateTime.endOf('month')

  return { startDateTime, endDateTime }
}

export const resolveSelectedDateFromQuery = ({
  selectedDay,
  timezone,
  datesWithAvailability,
  currentTime = DateTime.local(),
}) => {
  if (!selectedDay || !timezone) return null

  const parsedUtc = parseSelectedDayUtc(selectedDay)
  if (!parsedUtc) return null

  if (!isSelectedDayWithinAvailabilityCap(selectedDay, currentTime)) return null

  const selectedIsoDate = parsedUtc.toISODate()
  const selectedDate = DateTime.fromISO(selectedIsoDate, {
    zone: timezone,
  }).startOf('day')
  const today = currentTime.setZone(timezone).startOf('day')
  if (selectedDate < today) return null

  if (!datesWithAvailability.includes(selectedIsoDate)) return null

  return selectedDate
}

export const initialAvailabilityDateRange = (
  currentTime = DateTime.local(),
) => {
  const startDateTime = DateTime.max(currentTime, currentTime.startOf('month'))
  const endDateTime = availabilityMaxEndDateTime(currentTime)

  return { startDateTime, endDateTime }
}

export const useMonthlyLessonAvailability = (
  locationId,
  lessonTypeId,
  includeDeleted,
  skip = false,
  queryOptions = {},
) => {
  const currentTime = useRef(DateTime.local())
  const { startDateTime, endDateTime } = initialAvailabilityDateRange(
    currentTime.current,
  )
  return useQuery(LessonAvailabilityQuery, {
    ...queryOptions,
    skip: skip || !locationId || !lessonTypeId,
    variables: {
      locationId,
      id: lessonTypeId,
      startDateTime,
      endDateTime,
      includeDeleted: includeDeleted || false,
    },
  })
}
